import { useCallback, useEffect, useState, type RefObject } from 'react';

/**
 * Browser Fullscreen API, for one video's wrapping element.
 *
 * Full screen promotes an EXISTING element to the top layer, so the renderer
 * inside keeps its connection: no remount, no second MJPEG request (the browser
 * cannot tear one down, so each remount would orphan a server generator). The
 * transport is deliberately not changed on entry.
 */

export function fullscreenSupported(doc: Document): boolean {
  return doc.fullscreenEnabled === true
    && typeof HTMLElement !== 'undefined'
    && typeof HTMLElement.prototype.requestFullscreen === 'function';
}

export function isFullscreenOf(doc: Document, el: Element | null): boolean {
  return !!el && doc.fullscreenElement === el;
}

export interface FullscreenState {
  supported: boolean;
  /** This element — not merely some element — is full screen. */
  active: boolean;
  toggle: () => void;
}

export function useFullscreen(targetRef: RefObject<HTMLElement | null>): FullscreenState {
  const [supported] = useState(() => fullscreenSupported(document));
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!supported) return;
    // Esc, F11 and the browser's own UI all leave full screen without asking
    // the page; this event is the only way the control learns of it.
    const sync = () => setActive(isFullscreenOf(document, targetRef.current));
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [supported, targetRef]);

  const toggle = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const pending = isFullscreenOf(document, el)
      ? document.exitFullscreen()
      : el.requestFullscreen();
    // Refused (no user gesture, a permissions policy, an iframe): nothing to do
    // but leave the control as it was — fullscreenchange never fires.
    Promise.resolve(pending).catch((err: unknown) => {
      console.warn('[fullscreen] request refused:', err);
    });
  }, [targetRef]);

  return { supported, active, toggle };
}
